import { useEffect, useRef, useState } from 'react';

export type ScrollDirection = 'up' | 'down' | 'idle';

/**
 * Tracks vertical scroll direction on the window, throttled to one read per
 * animation frame, and returns to `'idle'` shortly after scrolling stops.
 *
 * Used by the CoachFab to collapse to an icon on scroll-down and re-expand on
 * scroll-up / at rest. Movements smaller than `threshold` px are ignored so a
 * jittery finger doesn't toggle the state.
 */
export function useScrollDirection(idleMs = 150, threshold = 4): ScrollDirection {
  const [dir, setDir] = useState<ScrollDirection>('idle');
  const lastY = useRef(0);
  const ticking = useRef(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    lastY.current = window.scrollY;
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY.current;
        if (Math.abs(delta) > threshold) {
          setDir(delta > 0 && y > 0 ? 'down' : 'up');
          lastY.current = y;
          clearTimeout(idleTimer.current);
          idleTimer.current = setTimeout(() => setDir('idle'), idleMs);
        }
        ticking.current = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(idleTimer.current);
    };
  }, [idleMs, threshold]);

  return dir;
}
